import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Sidebar from './components/Sidebar';
import Toolbar from './components/Toolbar';
import CanvasViewer from './components/CanvasViewer';
import InfoPanel from './components/InfoPanel';
import ControlPanel from './components/ControlPanel';
import Scalebar from './components/ScaleBar';
import LoadingOverlay from './components/LoadingOverlay';
import ErrorOverlay from './components/ErrorOverlay';
import InitialMessage from './components/InitialMessage';
import PlotOverlay from './components/PlotOverlay';
import StatsPlot from './components/StatsPlot';


const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.awd.bio/api';
console.debug("API_BASE:", API_BASE);



const HistologyViewer = () => {
  // State Hooks
  const [slideInfo, setSlideInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(0.001);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [tiles, setTiles] = useState(new Map());
  const [mrxsFiles, setMrxsFiles] = useState([]);
  const [filesError, setFilesError] = useState(null);
  const [cellFeatures, setCellFeatures] = useState([]);
  const [userAnnotations, setUserAnnotations] = useState([]);
  const [annotationName, setAnnotationName] = useState("");
  const [alphaBoundaries, setAlphaBoundaries] = useState({ ymin: 0, ymax: 0, xmin: 0, xmax: 0 });
  const [brightness, setBrightness] = useState(5);
  const [brightnessMask, setBrightnessMask] = useState(1);
  const [redThreshold, setRedThreshold] = useState(0.5);
  const [greenThreshold, setGreenThreshold] = useState(1);
  const [entropyThreshold, setEntropyThreshold] = useState(4); // Add this line
  const [isLassoMode, setIsLassoMode] = useState(false);
  const [isDrawingLasso, setIsDrawingLasso] = useState(false);
  const [lassoPoints, setLassoPoints] = useState([]);
  const [cropping, setCropping] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [contourShift, setContourShift] = useState({ x: 0, y: 0 });
  const [isAligning, setIsAligning] = useState(false);
  const [isShifting, setIsShifting] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Default to closed on mobile
  const [isPlotVisible, setIsPlotVisible] = useState(false); // New state for plot overlay
  const [showControls, setShowControls] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [associatedImages, setAssociatedImages] = useState([]);


  const activeSlideRef = useRef(null); // Ref for the active slide element
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [touchDistance, setTouchDistance] = useState(null);

  // Refs
  const canvasRef = useRef(null);
  const annotationCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const annotationAbortControllerRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Refs for touch gestures
  const lastTapTime = useRef(0);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const isPotentialDoubleTap = useRef(false);
  const lastDragY = useRef(0);

  // **LOGIC FIX**: Ref to store original contour shift before aligning
  const originalContourShiftRef = useRef(null);


  // Constants
  const TILE_SIZE = 256;
  const MIN_SCALE_CELLS = 0.1;

  // Memoized Calculations
  const scalebarInfo = useMemo(() => {
    if (!slideInfo || !slideInfo.mpp_x || scale <= 0) return null;
    const mpp = slideInfo.mpp_x;
    const targetPixelWidth = 150;
    const realWorldWidthMicrons = (targetPixelWidth / scale) * mpp;
    const exponent = Math.floor(Math.log10(realWorldWidthMicrons));
    const fraction = realWorldWidthMicrons / Math.pow(10, exponent);
    let niceFraction;
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3.5) niceFraction = 2;
    else if (fraction < 7.5) niceFraction = 5;
    else niceFraction = 10;
    const niceMicrons = niceFraction * Math.pow(10, exponent);
    const pixelWidth = (niceMicrons / mpp) * scale;
    let label = `${niceMicrons} µm`;
    if (niceMicrons >= 1000) {
      label = `${niceMicrons / 1000} mm`;
    }
    return { pixelWidth, label };
  }, [slideInfo, scale]);

  const cursorStyle = useMemo(() =>
    isAligning ? 'move' : isLassoMode ? 'crosshair' : (isDragging || isShifting ? 'grabbing' : 'grab'),
    [isAligning, isLassoMode, isDragging, isShifting]
  );

  const fetchAssociatedImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/associated_images`);
      if (response.ok) {
        const data = await response.json();
        setAssociatedImages(data.images || []);
      } else {
        console.error('Failed to fetch associated images');
        setAssociatedImages([]);
      }
    } catch (err) {
      console.error('Error fetching associated images:', err);
      setAssociatedImages([]);
    }
  }, []);
  const loadSlide = useCallback(async (filePath) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setTiles(new Map());
    setCellFeatures([]); // Clear old cell data
    setUserAnnotations([]); // Clear old user annotations
    setAssociatedImages([]); // Clear old associated images
    setLassoPoints([]);
    setSelectedSlide(filePath);
    setIsAligning(false);

    try {
      const response = await fetch(`${API_BASE}/load_slide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: filePath }),
        signal: controller.signal,
      });
      const data = await response.json();

      if (data.success) {
        setSlideInfo(data.slide_info);
        setContourShift(data.slide_info.contour_shift || { x: 50, y: 120 });

        // Separate user annotations from cell data
        if (data.slide_info.annotations && data.slide_info.annotations.features) {
          const userAnns = data.slide_info.annotations.features.filter(
            f => f.properties.objectType === 'annotation' || f.properties.name
          );
          setUserAnnotations(userAnns);
        }

        if (containerRef.current) {
          const containerHeight = containerRef.current.getBoundingClientRect().height;
          const { ymin, ymax } = data.slide_info.alpha_boundaries;
          setAlphaBoundaries(data.slide_info.alpha_boundaries);
          const slideHeight = ymax - ymin;
          const initialScale = containerHeight / slideHeight;
          setScale(initialScale);
          setPan({ x: 0, y: -ymin * initialScale });
        }

        // Fetch associated images
        fetchAssociatedImages();
      } else {
        setError(data.error || 'Failed to load slide');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Slide load was aborted.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchAssociatedImages]);

  const handlePlotPointClick = useCallback((slidePath) => {
    if (slidePath && slidePath !== selectedSlide) {
      console.debug(`Plot point clicked. Loading slide: ${slidePath}`);
      loadSlide(slidePath);
    } else {
      console.log(`Slide is already loaded or path is invalid: ${slidePath}`);
    }
  }, [selectedSlide, loadSlide]);


  // Centralized function to handle point clicks in the plot
  const plotProps = useMemo(() => ({
    redThreshold,
    greenThreshold,
    API_BASE,
    onPointClick: (path) => {
      handlePlotPointClick(path);
    },
    isDarkMode,
    selectedSlidePath: selectedSlide
  }), [redThreshold, greenThreshold, handlePlotPointClick, isDarkMode, selectedSlide]);


  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const isDark = mediaQuery.matches;
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      setIsDarkMode(isDark);
    };
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);


  const getOpenSlideLevel = useCallback(() => {
    if (!slideInfo || scale <= 0) return 0;
    const targetDownsample = 1 / scale;
    for (let i = 0; i < slideInfo.level_count; i++) {
      if (slideInfo.level_downsamples[i] >= targetDownsample) {
        return i;
      }
    }
    return slideInfo.level_count - 1;
  }, [slideInfo, scale]);

  const getVisibleTiles = useCallback(() => {
    if (!slideInfo || !containerRef.current || scale <= 0) return [];

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const { width: containerWidth, height: containerHeight } = rect;

    const level = getOpenSlideLevel();
    const downsample = slideInfo.level_downsamples[level];
    const tileWorldSize = TILE_SIZE * downsample;

    const viewLeftWorld = -pan.x / scale;
    const viewTopWorld = -pan.y / scale;
    const viewRightWorld = (containerWidth - pan.x) / scale;
    const viewBottomWorld = (containerHeight - pan.y) / scale;

    const startX = Math.floor(viewLeftWorld / tileWorldSize) - 1;
    const startY = Math.floor(viewTopWorld / tileWorldSize) - 1;
    const endX = Math.ceil(viewRightWorld / tileWorldSize) + 1;
    const endY = Math.ceil(viewBottomWorld / tileWorldSize) + 1;

    const [maxLevelX, maxLevelY] = slideInfo.level_dimensions[level];
    const maxTileX = Math.ceil(maxLevelX / TILE_SIZE);
    const maxTileY = Math.ceil(maxLevelY / TILE_SIZE);

    const visibleTiles = [];
    for (let y = Math.max(0, startY); y < Math.min(endY, maxTileY); y++) {
      for (let x = Math.max(0, startX); x < Math.min(endX, maxTileX); x++) {
        visibleTiles.push({ level, x, y, tileKey: `${level}-${x}-${y}` });
      }
    }
    return visibleTiles;
  }, [slideInfo, scale, pan, getOpenSlideLevel]);

  const loadTile = useCallback(async (level, x, y, tileKey, slidePath) => {
    if (!slidePath) return;
    if (tiles.has(tileKey) && (tiles.get(tileKey) instanceof Image || tiles.get(tileKey) === 'loading')) {
      return;
    }
    setTiles(prev => new Map(prev).set(tileKey, 'loading'));

    try {
      const url = `${API_BASE}/tile/${level}/${x}/${y}/${TILE_SIZE}?path=${encodeURIComponent(slidePath)}&dark_mode=${isDarkMode}`;
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => setTiles(prev => new Map(prev).set(tileKey, img));
        img.src = imageUrl;
      } else {
        setTiles(prev => new Map(prev).set(tileKey, 'failed'));
      }
    } catch (err) {
      setTiles(prev => new Map(prev).set(tileKey, 'failed'));
    }
  }, [tiles, isDarkMode]);

  const requestLoadVisibleTiles = useCallback(() => {
    if (!slideInfo || !selectedSlide) return;
    getVisibleTiles().forEach(({ level, x, y, tileKey }) => {
      loadTile(level, x, y, tileKey, selectedSlide);
    });
  }, [slideInfo, getVisibleTiles, loadTile, selectedSlide]);

  const renderTiles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !slideInfo || !containerRef.current) return;
    setError(null);
    const ctx = canvas.getContext('2d');
    const rect = containerRef.current.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    ctx.fillStyle = '#f8f8f8';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = false;
    const level = getOpenSlideLevel();
    const downsample = slideInfo.level_downsamples[level];
    const tileDrawSize = TILE_SIZE * downsample * scale;
    getVisibleTiles().forEach(({ x, y, tileKey }) => {
      const worldX = x * TILE_SIZE * downsample;
      const worldY = y * TILE_SIZE * downsample;
      const drawX = worldX * scale + pan.x;
      const drawY = worldY * scale + pan.y;
      const tile = tiles.get(tileKey);
      if (tile instanceof Image) {
        ctx.drawImage(tile, drawX, drawY, Math.ceil(tileDrawSize) + 1, Math.ceil(tileDrawSize) + 1);
      } else {
        ctx.fillStyle = '#e8e8e8';
        ctx.fillRect(drawX, drawY, Math.ceil(tileDrawSize), Math.ceil(tileDrawSize));
      }
    });
  }, [slideInfo, scale, pan, tiles, getVisibleTiles, getOpenSlideLevel]);


  const renderAnnotations = useCallback(() => {
    const canvas = annotationCanvasRef.current;
    if (!canvas || !slideInfo || !containerRef.current) return;
    const ctx = canvas.getContext('2d');
    const rect = containerRef.current.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const featuresToRender = [...cellFeatures, ...userAnnotations];

    if (featuresToRender.length > 0) {
      const viewLeftWorld = -pan.x / scale;
      const viewTopWorld = -pan.y / scale;
      const viewRightWorld = (rect.width - pan.x) / scale;
      const viewBottomWorld = (rect.height - pan.y) / scale;

      const offsetX = alphaBoundaries.xmin + contourShift.x;
      const offsetY = alphaBoundaries.ymin + contourShift.y;

      featuresToRender.forEach(feature => {
        if (!feature.geometry) return;

        // **** CHANGE: Filter low entropy regions ****
        if (
          (feature.properties.objectType === 'annotation' || feature.properties.name) &&
          feature.properties.hasOwnProperty('entropy') &&
          feature.properties.entropy < entropyThreshold
        ) {
          return; // Skip rendering this low-entropy region
        }

        if (feature.geometry.type === 'Polygon') {
          const coordinates = feature.geometry.coordinates[0];
          if (coordinates.length === 0) return;

          let polyMinX = Infinity, polyMinY = Infinity, polyMaxX = -Infinity, polyMaxY = -Infinity;
          for (const point of coordinates) {
            const worldX = point[0] + offsetX;
            const worldY = point[1] + offsetY;
            if (worldX < polyMinX) polyMinX = worldX;
            if (worldY < polyMinY) polyMinY = worldY;
            if (worldX > polyMaxX) polyMaxX = worldX;
            if (worldY > polyMaxY) polyMaxY = worldY;
          }
          if (polyMaxX < viewLeftWorld || polyMinX > viewRightWorld || polyMaxY < viewTopWorld || polyMinY > viewBottomWorld) {
            return;
          }

          ctx.beginPath();
          const firstPoint = coordinates[0];
          ctx.moveTo(
            (firstPoint[0] + offsetX) * scale + pan.x,
            (firstPoint[1] + offsetY) * scale + pan.y
          );
          // const step = scale < 0.01 ? 2 : 1;
          const step = 1;
          for (let i = 1; i < coordinates.length; i += step) {
            const point = coordinates[i];
            ctx.lineTo(
              (point[0] + offsetX) * scale + pan.x,
              (point[1] + offsetY) * scale + pan.y
            );
          }
          ctx.closePath();

        } else if (feature.geometry.type === 'Point') {
          const [px, py] = feature.geometry.coordinates;
          const worldX = px + offsetX;
          const worldY = py + offsetY;

          if (worldX < viewLeftWorld || worldX > viewRightWorld || worldY < viewTopWorld || worldY > viewBottomWorld) {
            return;
          }

          const drawX = worldX * scale + pan.x;
          const drawY = worldY * scale + pan.y;
          ctx.fillRect(drawX - 1, drawY - 1, 3, 3);
        }

        if (feature.properties.objectType === 'annotation' || feature.properties.name) {
          if (feature.properties.entropy) {
            const entropy = feature.properties.entropy;
            const alpha = Math.min(1, Math.max(0, (entropy - 4) / 4));
            ctx.fillStyle = `rgba(255, 255, 0, ${alpha})`;
            ctx.strokeStyle = `rgba(0, 100, 250, 0.2)`;
            ctx.lineWidth = 2;
          } else {
            ctx.strokeStyle = 'rgba(0, 100, 255, 0.8)';
            ctx.fillStyle = 'rgba(0, 100, 255, 0.0)';
            ctx.lineWidth = 5;
          }

          ctx.stroke();
          ctx.fill();

          if (feature.properties.entropy && scale > 0.05 && feature.geometry.type === 'Polygon') {
            const coordinates = feature.geometry.coordinates[0];
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (const point of coordinates) {
                if (point[0] < minX) minX = point[0];
                if (point[1] < minY) minY = point[1];
                if (point[0] > maxX) maxX = point[0];
                if (point[1] > maxY) maxY = point[1];
            }
            const centerX = minX + (maxX - minX) / 2;
            const centerY = minY + (maxY - minY) / 2;

            const textX = (centerX + offsetX) * scale + pan.x;
            const textY = (centerY + offsetY) * scale + pan.y;

            ctx.font = "14px sans-serif";
            ctx.fillStyle = 'white';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${feature.id}: ${feature.properties.entropy.toFixed(2)}`, textX, textY);
          }

        } else if (feature.properties.objectType === 'cell') {
          ctx.lineWidth = 1.5;
          const m = feature.properties.measurements;
          const isRedPositive = m["Nucleus: Red max"] / m["Nucleus: Blue mean"] > redThreshold;
          const isGreenPositive = (m["Nucleus: Green max"] - m["Cell: Green min"]) / m["Nucleus: Blue mean"] > greenThreshold;

          let color;
          if (isRedPositive && isGreenPositive) color = '#FFFF00';
          else if (isRedPositive) color = '#FF1744';
          else if (isGreenPositive) color = '#39FF14';
          else color = '#6f6f6f8f';

          if (feature.geometry.type === 'Polygon') {
            ctx.strokeStyle = color;
            ctx.fillStyle = color.length === 9 ? color.slice(0, 7) + '03' : color + '03';
            ctx.stroke();
            ctx.fill();
          } else {
            ctx.fillStyle = color;
          }
        }
      });
    }

    if (isDrawingLasso && lassoPoints.length > 1) {
      ctx.strokeStyle = 'rgba(255, 20, 20, 0.9)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(lassoPoints[0].x * scale + pan.x, lassoPoints[0].y * scale + pan.y);
      for (let i = 1; i < lassoPoints.length; i++) {
        ctx.lineTo(lassoPoints[i].x * scale + pan.x, lassoPoints[i].y * scale + pan.y);
      }
      ctx.stroke();
    }
  }, [userAnnotations, slideInfo, scale, pan, cellFeatures, isDrawingLasso, lassoPoints, alphaBoundaries, redThreshold, greenThreshold, contourShift, entropyThreshold]);


  const handleZoom = useCallback((delta, mouseX, mouseY) => {
    if (!slideInfo || !containerRef.current) return;
    const scaleMultiplier = Math.pow(2, delta);
    const newScale = scale * scaleMultiplier;
    const maxScale = 2.0;
    const minScale = containerRef.current.getBoundingClientRect().width / slideInfo.level_dimensions[0][0] / 2;
    const clampedScale = Math.max(minScale, Math.min(maxScale, newScale));
    if (clampedScale !== scale) {
      const actualScaleFactor = clampedScale / scale;
      const newPan = {
        x: (pan.x - mouseX) * actualScaleFactor + mouseX,
        y: (pan.y - mouseY) * actualScaleFactor + mouseY,
      };
      setPan(newPan);
      setScale(clampedScale);
    }
  }, [scale, pan, slideInfo]);

  const handleMouseDown = useCallback((e) => {
    if (isAligning) {
      setIsShifting(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    } else if (isLassoMode) {
      setIsDrawingLasso(true);
      setLassoPoints([]);
    } else {
      setIsDragging(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  }, [isLassoMode, isAligning]);

  const handleMouseMove = useCallback((e) => {
    if (isShifting) {
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;
      setContourShift(prev => ({
        x: prev.x + deltaX / scale,
        y: prev.y + deltaY / scale
      }));
      setLastMousePos({ x: e.clientX, y: e.clientY });
    } else if (isDragging) {
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;
      setPan(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
      setLastMousePos({ x: e.clientX, y: e.clientY });
    } else if (isDrawingLasso) {
      const rect = annotationCanvasRef.current.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      const slideX = (canvasX - pan.x) / scale;
      const slideY = (canvasY - pan.y) / scale;
      setLassoPoints(prev => [...prev, { x: slideX, y: slideY }]);
    }
  }, [isDragging, lastMousePos, isDrawingLasso, pan, scale, isShifting]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsShifting(false);
    if (isDrawingLasso) {
      setIsDrawingLasso(false);
    }
  }, [isDrawingLasso]);

  const handleTouchStart = useCallback((e) => {
    const touches = e.touches;
    const now = new Date().getTime();

    if (isAligning) {
      if (touches.length === 1) {
        setIsShifting(true);
        setLastMousePos({ x: touches[0].clientX, y: touches[0].clientY });
      }
      return;
    }

    if (touches.length === 1) {
      const touch = touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
      const timeSinceLastTap = now - lastTapTime.current;

      if (timeSinceLastTap < 150) {
        e.preventDefault();
        isPotentialDoubleTap.current = true;
        lastTapTime.current = 0;
      } else {
        setIsDragging(true);
        setLastMousePos({ x: touch.clientX, y: touch.clientY });
      }
    } else if (touches.length === 2) {
      setIsDragging(false);
      isPotentialDoubleTap.current = false;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      setTouchDistance(Math.sqrt(dx * dx + dy * dy));
    }
  }, [isAligning]);

  const handleTouchMove = useCallback((e) => {
    const touches = e.touches;
    if (!annotationCanvasRef.current) return;
    const rect = annotationCanvasRef.current.getBoundingClientRect();

    if (isShifting && touches.length === 1) {
      e.preventDefault();
      const deltaX = touches[0].clientX - lastMousePos.x;
      const deltaY = touches[0].clientY - lastMousePos.y;
      setContourShift(prev => ({
        x: prev.x + deltaX / scale,
        y: prev.y + deltaY / scale
      }));
      setLastMousePos({ x: touches[0].clientX, y: touches[0].clientY });
      return;
    }

    if (isPotentialDoubleTap.current && touches.length === 1) {
      e.preventDefault();
      setIsDragging(false);
      const currentY = touches[0].clientY;
      const startY = touchStartPos.current.y;
      const prevY = lastDragY.current === 0 ? startY : lastDragY.current;
      const deltaY = currentY - prevY;

      if (Math.abs(currentY - startY) > 5) {
        handleZoom(deltaY * -0.01, rect.width / 2, rect.height / 2);
        lastDragY.current = currentY;
      }
    } else if (touches.length === 2 && touchDistance) {
      e.preventDefault();
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      const newTouchDistance = Math.sqrt(dx * dx + dy * dy);
      const scaleMultiplier = newTouchDistance / touchDistance;

      const newScale = scale * scaleMultiplier;
      const maxScale = 2.0;
      const minScale = containerRef.current.getBoundingClientRect().width / slideInfo.level_dimensions[0][0] / 2;
      const clampedScale = Math.max(minScale, Math.min(maxScale, newScale));

      if (clampedScale !== scale) {
        const mouseX = (touches[0].clientX + touches[1].clientX) / 2 - rect.left;
        const mouseY = (touches[0].clientY + touches[1].clientY) / 2 - rect.top;
        const actualScaleFactor = clampedScale / scale;
        const newPan = {
          x: (pan.x - mouseX) * actualScaleFactor + mouseX,
          y: (pan.y - mouseY) * actualScaleFactor + mouseY,
        };
        setPan(newPan);
        setScale(clampedScale);
        setTouchDistance(newTouchDistance);
      }
    } else if (isDragging && touches.length === 1) {
      e.preventDefault();
      const deltaX = touches[0].clientX - lastMousePos.x;
      const deltaY = touches[0].clientY - lastMousePos.y;
      setPan(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
      setLastMousePos({ x: touches[0].clientX, y: touches[0].clientY });
    }
  }, [isDragging, lastMousePos, pan, scale, slideInfo, touchDistance, handleZoom, isShifting]);

  const handleTouchEnd = useCallback((e) => {
    const now = new Date().getTime();

    if (isPotentialDoubleTap.current) {
      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartPos.current.x;
      const dy = touch.clientY - touchStartPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 10) {
        if (!annotationCanvasRef.current) return;
        const rect = annotationCanvasRef.current.getBoundingClientRect();
        const mouseX = touch.clientX - rect.left;
        const mouseY = touch.clientY - rect.top;
        handleZoom(1, mouseX, mouseY);
      }
      isPotentialDoubleTap.current = false;
    }

    lastTapTime.current = now;

    setIsDragging(false);
    setTouchDistance(null);
    lastDragY.current = 0;
    setIsShifting(false);
  }, [handleZoom]);

  const fetchCellData = useCallback(async (filePath, currentScale) => {
    if (annotationAbortControllerRef.current) {
      annotationAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    annotationAbortControllerRef.current = controller;

    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    const viewLeftWorld = -pan.x / currentScale;
    const viewTopWorld = -pan.y / currentScale;
    const viewRightWorld = (rect.width - pan.x) / currentScale;
    const viewBottomWorld = (rect.height - pan.y) / currentScale;

    const offsetX = alphaBoundaries.xmin + contourShift.x;
    const offsetY = alphaBoundaries.ymin + contourShift.y;

    const bounds = {
      xmin: viewLeftWorld - offsetX,
      ymin: viewTopWorld - offsetY,
      xmax: viewRightWorld - offsetX,
      ymax: viewBottomWorld - offsetY,
    };

    const isHighZoom = currentScale >= MIN_SCALE_CELLS;
    const endpoint = isHighZoom ? `${API_BASE}/contours_chunk` : `${API_BASE}/centers`;
    const body = JSON.stringify({ path: filePath, bounds: bounds });

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        signal: controller.signal,
      });
      const data = await response.json();
      if (response.ok && !controller.signal.aborted) {
        setCellFeatures(data.features);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(`Error fetching ${isHighZoom ? 'contours' : 'centers'}:`, err);
      }
    }
  }, [pan, contourShift, alphaBoundaries]);

  // **LOGIC FIX**: New functions to handle alignment state
  const startAlignment = () => {
    originalContourShiftRef.current = contourShift;
    setIsAligning(true);
  };

  const cancelAlignment = () => {
    if (originalContourShiftRef.current) {
      setContourShift(originalContourShiftRef.current);
    }
    setIsAligning(false);
  };

  const handleSaveShift = async () => {
    if (!selectedSlide) {
      alert("No slide selected to save alignment for.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/contour_shift`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: selectedSlide, shift: contourShift }),
      });
      if (response.ok) {
        console.log("Contour shift saved successfully!");
      } else {
        const errData = await response.json();
        alert(`Error saving alignment: ${errData.error}`);
      }
    } catch (err) {
      alert(`An error occurred: ${err.message}`);
    } finally {
      setIsAligning(false);
    }
  };

  const resetView = () => {
    if (!slideInfo || !containerRef.current) return;
    const containerHeight = containerRef.current.getBoundingClientRect().height;
    const { ymin, ymax } = alphaBoundaries;
    const slideHeight = ymax - ymin;
    const initialScale = containerHeight / slideHeight;
    setScale(initialScale);
    setPan({ x: 0, y: -ymin * initialScale });
  };

  const fetchMrxsFiles = useCallback(async () => {
    setFilesError(null);
    try {
      const response = await fetch(`${API_BASE}/list_mrxs_files`);
      const data = await response.json();
      if (response.ok) {
        setMrxsFiles(data.files);
        setError(null);
      } else {
        setFilesError(data.error || 'Failed to fetch files');
      }
    } catch (err) {
      setFilesError(err.message || 'Network error');
    }
  }, []);

  const handleSaveAnnotation = async () => {
    if (lassoPoints.length < 3 || !annotationName) {
      alert("Please draw a selection and enter a name.");
      return;
    }
    setCropping(true);
    try {
      const response = await fetch(`${API_BASE}/annotations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ points: lassoPoints, name: annotationName }),
      });
      if (response.ok) {
        const { feature } = await response.json();
        setUserAnnotations(prev => [...prev, feature]); // Add to user annotations state
        setLassoPoints([]);
        setAnnotationName("");
      } else {
        const errData = await response.json();
        alert(`Error saving annotation: ${errData.error}`);
      }
    } catch (err) {
      alert(`An error occurred: ${err.message}`);
    } finally {
      setCropping(false);
    }
  };

  useEffect(() => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const wheelHandler = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      handleZoom(delta, mouseX, mouseY);
    };
    canvas.addEventListener('wheel', wheelHandler, { passive: false });
    return () => canvas.removeEventListener('wheel', wheelHandler);
  }, [handleZoom]);

  useEffect(() => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    const animationFrameId = requestAnimationFrame(() => {
      renderTiles();
      renderAnnotations();
    });
    return () => cancelAnimationFrame(animationFrameId);
  }, [renderTiles, renderAnnotations]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (slideInfo) {
        requestLoadVisibleTiles();
        fetchCellData(selectedSlide, scale);
      }
    }, isDragging ? 200 : 150);
    return () => clearTimeout(debounceTimer);
  }, [slideInfo, selectedSlide, pan, scale, isDragging, requestLoadVisibleTiles, fetchCellData]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      requestLoadVisibleTiles();
    });

    resizeObserver.observe(container);

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, [requestLoadVisibleTiles]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!selectedSlide || loading) return;

      const params = new URLSearchParams();
      const slideName = selectedSlide.split('/').pop();

      if (slideName) {
        params.set('slide', slideName);
      }
      params.set('red', redThreshold.toFixed(2));
      params.set('green', greenThreshold.toFixed(2));
      params.set('panX', pan.x.toFixed(2));
      params.set('panY', pan.y.toFixed(2));
      params.set('scale', scale.toFixed(4));

      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);

    }, 500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [selectedSlide, redThreshold, greenThreshold, pan, scale, loading]);

  useEffect(() => {
    const initializeFromUrl = async () => {
      if (isInitialLoad.current && mrxsFiles.length > 0 && !selectedSlide) {
        isInitialLoad.current = false;

        const params = new URLSearchParams(window.location.search);
        const slideNameFromUrl = params.get('slide');

        if (slideNameFromUrl) {
          const slideToLoad = mrxsFiles.find(f => f.name === slideNameFromUrl);
          if (slideToLoad) {
            await loadSlide(slideToLoad.path);

            const red = parseFloat(params.get('red'));
            const green = parseFloat(params.get('green'));
            const panX = parseFloat(params.get('panX'));
            const panY = parseFloat(params.get('panY'));
            const scaleVal = parseFloat(params.get('scale'));

            if (!isNaN(red)) setRedThreshold(red);
            if (!isNaN(green)) setGreenThreshold(green);
            if (!isNaN(panX) && !isNaN(panY)) setPan({ x: panX, y: panY });
            if (!isNaN(scaleVal) && scaleVal > 0) setScale(scaleVal);
          }
        }
      }
    };
    initializeFromUrl();
  }, [mrxsFiles, selectedSlide, loadSlide]);

  useEffect(() => {
    fetchMrxsFiles();
  }, [fetchMrxsFiles]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (isSidebarOpen && activeSlideRef.current) {
      setTimeout(() => {
        activeSlideRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [isSidebarOpen, selectedSlide]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleResize = () => setIsSidebarOpen(mediaQuery.matches);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  return (
    <>
      <script defer src="https://umami-x04ogg4c0w0gc4go4wk8w8oc.awd.run/script.js" data-website-id="65e3971d-88db-4cd6-8103-9531f98d57be"></script>
      <div className="w-full h-screen bg-gray-100 dark:bg-gray-900 flex flex-col">
        <Toolbar
          slideInfo={slideInfo}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          onTogglePlot={() => setIsPlotVisible(!isPlotVisible)}
          onToggleControls={() => setShowControls(!showControls)}
          scale={scale}
          lassoProps={{
            isLassoMode,
            setIsLassoMode,
            lassoPoints,
            setLassoPoints,
            isDrawingLasso,
            annotationName,
            setAnnotationName,
            handleSaveAnnotation,
            cropping
          }}
          onResetView={resetView}
          onToggleInfo={() => setShowInfo(!showInfo)}
        />

        {isSidebarOpen && (
          <div
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 bg-black/50 z-20 hidden"
          />
        )}

        <div className="flex-1 flex flex-row overflow-hidden">
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            files={mrxsFiles}
            filesError={filesError}
            selectedSlide={selectedSlide}
            onLoadSlide={(path) => {
              loadSlide(path);
              if (window.innerWidth < 768) {
                setIsSidebarOpen(false);
              }
            }}
            activeSlideRef={activeSlideRef}
            plotProps={plotProps}
            StatsPlot={StatsPlot}
          />

          <main className="flex-1 flex flex-col relative ">
            <div className="flex-1 relative" ref={containerRef}>
              <LoadingOverlay loading={loading} />
              <ErrorOverlay error={error} />
              <InitialMessage show={!slideInfo && !loading && !error} />

              {slideInfo && (
                <>
                  <CanvasViewer
                    canvasRef={canvasRef}
                    annotationCanvasRef={annotationCanvasRef}
                    brightness={brightness}
                    brightnessMask={brightnessMask}
                    cursorStyle={cursorStyle}
                    handlers={{
                      onMouseDown: handleMouseDown,
                      onMouseMove: handleMouseMove,
                      onMouseUp: handleMouseUp,
                      onMouseLeave: handleMouseUp,
                    }}
                  />
                    <Scalebar info={scalebarInfo} />
                  </>
              )}
                  <div className="absolute top-4 right-4 z-10 w-xxs h-fit flex flex-col  space-y-2 overflow-auto max-h-[90%]">
                    <InfoPanel
                      show={showInfo}
                      slideInfo={slideInfo}
                      userAnnotations={userAnnotations}
                      cellFeatures={cellFeatures}
                      associatedImages={associatedImages}
                    />
                    <ControlPanel
                      show={showControls}
                      slideInfo={slideInfo}
                      controls={{
                        brightness, setBrightness,
                        brightnessMask, setBrightnessMask,
                        redThreshold, setRedThreshold,
                        greenThreshold, setGreenThreshold,
                        entropyThreshold, setEntropyThreshold, 
                        contourShift, isAligning,
                        startAlignment, cancelAlignment, handleSaveShift
                      }}

                    />
                    <PlotOverlay
                      isVisible={isPlotVisible}
                      onClose={() => setIsPlotVisible(false)}
                      plotProps={{
                        redThreshold,
                        greenThreshold,
                        API_BASE,
                        onPointClick: (path) => {
                          handlePlotPointClick(path);
                        },
                        isDarkMode,
                        selectedSlidePath: selectedSlide
                      }}
                    />
                  </div>
            </div>
          </main>
        </div>


      </div>
    </>

  );
};

export default HistologyViewer;